import {
  centsToRatio,
  clamp,
  clamp01,
  createAdvancedVoice,
  createBodyResonatorBank,
  createKarplusStrongString,
  createModalBank,
  createNoiseBurst,
  hashSeed,
} from "./dspPrimitives";
import { getVariantModelProfile } from "./instrumentModelProfiles";
import type { AdvancedSynthesisInput, AdvancedVoiceHandle, ModalSpec, ResonatorSpec } from "./types";

function controlNumber(input: AdvancedSynthesisInput, id: string, fallback: number): number {
  const value = input.controls[id];

  return typeof value === "number" ? clamp01(value) : fallback;
}

function scheduleDelayPitch(delay: DelayNode, input: AdvancedSynthesisInput, points: Array<{ time: number; cents: number }>): void {
  if (points.length === 0) {
    return;
  }

  const base = clamp(1 / Math.max(24, input.frequency), 0.001, 1 / 24);

  delay.delayTime.cancelScheduledValues(input.now);
  delay.delayTime.setValueAtTime(base / centsToRatio(points[0].cents), input.now + points[0].time);

  for (const point of points.slice(1)) {
    delay.delayTime.linearRampToValueAtTime(base / centsToRatio(point.cents), input.now + point.time);
  }
}

function makeWaveshaper(context: BaseAudioContext, amount: number): WaveShaperNode {
  const shaper = context.createWaveShaper();
  const length = 512;
  const curve = new Float32Array(length);
  const drive = 1 + amount * 14;

  for (let index = 0; index < length; index += 1) {
    const x = (index / (length - 1)) * 2 - 1;

    curve[index] = Math.tanh(x * drive) * (0.72 + amount * 0.22);
  }

  shaper.curve = curve;
  shaper.oversample = "2x";

  return shaper;
}

function connectClick(
  input: AdvancedSynthesisInput,
  destination: AudioNode,
  frequency: number,
  gain: number,
  duration: number,
  q = 2.5
): { source: AudioBufferSourceNode; cleanupNodes: AudioNode[] } {
  const click = createNoiseBurst(input.context, {
    duration,
    attack: 0.0006,
    decay: 2.8,
    gain,
    filterType: "bandpass",
    frequency,
    q,
    seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, input.ornament, frequency),
    tilt: "bright",
    startTime: input.now
  });

  click.output.connect(destination);
  click.source.start(input.now);

  return { source: click.source, cleanupNodes: click.cleanupNodes };
}

function gayageumBodyModes(frequency: number, variantBrightness: number): ResonatorSpec[] {
  return [
    { frequency: clamp(frequency * 0.5, 90, 160), q: 1.3, gainDb: 3.5, type: "peaking" },
    { frequency: clamp(frequency * 0.92, 180, 280), q: 1.6, gainDb: 4.2, type: "peaking" },
    { frequency: clamp(frequency * 1.75, 420, 650), q: 2.0, gainDb: 3.2, type: "peaking" },
    { frequency: 1200 + variantBrightness * 1300, q: 2.7, gainDb: 2.4, type: "peaking" }
  ];
}

export function renderGayageum(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const profile = getVariantModelProfile("gayageum", input.variantId);
  const variant = profile ?? getVariantModelProfile("gayageum");
  const gain = context.createGain();
  const dry = context.createGain();
  const sources: AudioScheduledSourceNode[] = [];
  const cleanupNodes: AudioNode[] = [gain, dry];
  const velocity = clamp01(input.velocity);
  const brightness = variant?.brightness ?? 0.58;
  const nonghyeon = input.ornament === "nonghyeon";
  const pressure = controlNumber(input, "nonghyeonDepth", controlNumber(input, "left_hand_pressure", 0.5));
  const feedback = 0.91 + (variant?.decay ?? 0.55) * 0.065;
  const string = createKarplusStrongString(context, input, {
    brightness,
    decay: 0.26 + (variant?.decay ?? 0.55) * 0.45,
    feedback,
    pluckPosition: 0.32 + brightness * 0.18,
    outputGain: 0.42 + velocity * 0.2
  });

  gain.gain.value = Math.max(0.12, velocity * 0.76);
  dry.gain.value = 0.74;
  string.output.connect(dry);
  dry.connect(gain);

  const click = connectClick(input, gain, 3600 + brightness * 3600, velocity * (0.035 + (variant?.noiseGain ?? 0.35) * 0.05), 0.008);
  const scrape = createNoiseBurst(context, {
    duration: 0.11,
    attack: 0.002,
    decay: 2.3,
    gain: velocity * (0.018 + pressure * 0.038),
    filterType: "bandpass",
    frequency: 1800 + brightness * 4200,
    q: 1.9,
    seed: hashSeed(input.instrumentId, input.variantId, "bridge-scrape", input.noteLabel),
    tilt: "bright",
    startTime: input.now + 0.004
  });

  scrape.output.connect(gain);
  scrape.source.start(input.now + 0.004);
  cleanupNodes.push(...string.cleanupNodes, ...click.cleanupNodes, ...scrape.cleanupNodes);
  sources.push(string.source, click.source, scrape.source);
  cleanupNodes.push(...createBodyResonatorBank(context, dry, gain, gayageumBodyModes(input.frequency, brightness), variant?.bodyGain ?? 0.56));

  if (nonghyeon) {
    const depth = 22 + pressure * 112 + velocity * 28;

    scheduleDelayPitch(string.delay, input, [
      { time: 0, cents: 0 },
      { time: 0.055, cents: depth },
      { time: 0.11, cents: depth * 0.25 },
      { time: 0.17, cents: depth * 0.84 },
      { time: 0.24, cents: depth * 0.18 },
      { time: 0.32, cents: depth * 0.58 }
    ]);
    string.dampingFilter.frequency.setValueAtTime(1800 + brightness * 3200, input.now);
    string.dampingFilter.frequency.linearRampToValueAtTime(3200 + pressure * 4700, input.now + 0.07);
    string.dampingFilter.frequency.linearRampToValueAtTime(2100 + brightness * 3200, input.now + 0.18);
  }

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: 0.34 + (variant?.decay ?? 0.55) * 0.34,
    debug: {
      family: "plucked_string_waveguide",
      model: "gayageum_karplus_body_modal",
      layers: ["pluck_click", "bridge_scrape", "wood_body_modes", nonghyeon ? "nonghyeon_pitch_brightness" : "plain"]
    }
  });
}

function oudBodyModes(frequency: number, variantId?: string): ResonatorSpec[] {
  const iraqi = variantId === "iraqi_oud";
  const turkish = variantId === "turkish_oud";

  return [
    { frequency: iraqi ? 170 : 135, q: 1.1, gainDb: turkish ? 2.4 : 4.8, type: "peaking" },
    { frequency: clamp(frequency * 0.75, 210, 340), q: 1.4, gainDb: iraqi ? 4.8 : 3.6, type: "peaking" },
    { frequency: turkish ? 940 : 680, q: 2.1, gainDb: turkish ? 3.5 : 2.4, type: "peaking" },
    { frequency: turkish ? 2450 : 1650, q: 2.6, gainDb: turkish ? 2.8 : 1.6, type: "peaking" }
  ];
}

export function renderOud(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const variant = getVariantModelProfile("oud", input.variantId);
  const gain = context.createGain();
  const sharedBodyIn = context.createGain();
  const sources: AudioScheduledSourceNode[] = [];
  const cleanupNodes: AudioNode[] = [gain, sharedBodyIn];
  const velocity = clamp01(input.velocity);
  const detuneBase =
    input.variantId === "turkish_oud" ? [-3.5, 4.5] : input.variantId === "iraqi_oud" ? [-4.5, 2.5] : [-3, 3.5];
  const attackWander = (variant?.modulationDepth ?? 0.45) * 8;
  const strings = detuneBase.map((cents, index) =>
    createKarplusStrongString(context, input, {
      frequency: input.frequency * centsToRatio(cents),
      brightness: variant?.brightness ?? 0.5,
      decay: 0.22 + (variant?.decay ?? 0.5) * 0.42,
      feedback: 0.9 + (variant?.decay ?? 0.5) * 0.06,
      pluckPosition: index === 0 ? 0.28 : 0.36,
      outputGain: 0.28,
      seedSuffix: `course-${index}`
    })
  );

  gain.gain.value = velocity * (0.62 + (variant?.bodyGain ?? 0.55) * 0.22);

  for (const [index, string] of strings.entries()) {
    string.output.connect(sharedBodyIn);
    scheduleDelayPitch(string.delay, input, [
      { time: 0, cents: detuneBase[index] + (index === 0 ? -attackWander : attackWander) },
      { time: 0.06, cents: detuneBase[index] - (index === 0 ? attackWander * 0.4 : attackWander * 0.35) },
      { time: 0.16, cents: detuneBase[index] }
    ]);
    sources.push(string.source);
    cleanupNodes.push(...string.cleanupNodes);
  }

  sharedBodyIn.connect(gain);
  cleanupNodes.push(...createBodyResonatorBank(context, sharedBodyIn, gain, oudBodyModes(input.frequency, input.variantId), variant?.bodyGain ?? 0.65));

  const risha = connectClick(input, gain, input.variantId === "turkish_oud" ? 4300 : 2900, velocity * 0.085, 0.006, 3.2);
  const thump = connectClick(input, gain, 180 + (input.variantId === "iraqi_oud" ? 45 : 0), velocity * 0.052, 0.045, 1.1);
  const scratch = createNoiseBurst(context, {
    duration: 0.055,
    attack: 0.001,
    decay: 2.7,
    gain: velocity * (0.024 + (variant?.noiseGain ?? 0.38) * 0.04),
    filterType: "bandpass",
    frequency: input.variantId === "turkish_oud" ? 4200 : 3000,
    q: 2.4,
    seed: hashSeed(input.instrumentId, input.variantId, "risha-scratch", input.noteLabel),
    tilt: "bright",
    startTime: input.now
  });

  scratch.output.connect(gain);
  scratch.source.start(input.now);
  sources.push(risha.source, thump.source, scratch.source);
  cleanupNodes.push(...risha.cleanupNodes, ...thump.cleanupNodes, ...scratch.cleanupNodes);

  if (input.ornament === "micro_bend") {
    const bend = 12 + controlNumber(input, "microBendAmount", 0.35) * 46;

    for (const string of strings) {
      scheduleDelayPitch(string.delay, input, [
        { time: 0, cents: 0 },
        { time: 0.1, cents: bend },
        { time: 0.22, cents: bend * 0.2 },
        { time: 0.34, cents: 0 }
      ]);
    }
  }

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: 0.32 + (variant?.decay ?? 0.5) * 0.3,
    debug: {
      family: "plucked_string_waveguide",
      model: "oud_dual_course_waveguide",
      variantTags: variant?.metadataTags,
      layers: ["dual_delay_lines", "risha_click", "scratch", "bowl_body"]
    }
  });
}

function sitarSympatheticModes(input: AdvancedSynthesisInput): ModalSpec[] {
  const ragaYaman = String(input.controls.tuningId ?? "").includes("yaman");
  const baseRatios = ragaYaman
    ? [1, 9 / 8, 5 / 4, 45 / 32, 3 / 2, 5 / 3, 15 / 8, 2, 9 / 4, 5 / 2, 3, 15 / 4]
    : [1, 16 / 15, 6 / 5, 4 / 3, 3 / 2, 8 / 5, 9 / 5, 2, 12 / 5, 3, 16 / 5, 4];

  return baseRatios.map((ratio, index) => ({
    ratio,
    gain: 0.012 + (index % 3) * 0.004,
    decay: 0.85 + index * 0.055,
    detuneCents: (index % 2 === 0 ? -1 : 1) * (2 + index * 0.2),
    type: "sine" as const
  }));
}

export function renderSitar(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const variant = getVariantModelProfile("sitar", input.variantId);
  const gain = context.createGain();
  const mainBus = context.createGain();
  const sources: AudioScheduledSourceNode[] = [];
  const cleanupNodes: AudioNode[] = [gain, mainBus];
  const meend = input.ornament === "meend";
  const meendDepth = meend ? 160 + controlNumber(input, "microBendAmount", 0.5) * 460 : 0;
  const brightness = clamp01((variant?.brightness ?? 0.7) + (meend ? 0.12 : 0));
  const main = createKarplusStrongString(context, input, {
    frequency: input.variantId === "surbahar" ? input.frequency * 0.5 : input.frequency,
    brightness,
    decay: 0.42 + (variant?.decay ?? 0.72) * 0.58,
    feedback: 0.925 + (variant?.decay ?? 0.72) * 0.055,
    pluckPosition: 0.22,
    outputGain: 0.38,
    seedSuffix: "main"
  });
  const jawariDrive = clamp01(0.42 + (variant?.noiseGain ?? 0.55) * 0.45 + (meend ? 0.24 : 0));
  const jawari = makeWaveshaper(context, jawariDrive);
  const jawariHigh = context.createBiquadFilter();
  const jawariGain = context.createGain();
  const sympathetic = createModalBank(context, input.frequency, sitarSympatheticModes(input), {
    destination: gain,
    velocity: input.velocity,
    now: input.now + 0.04,
    family: "plucked_string_waveguide",
    quality: input.quality,
    totalGain: 1,
    duration: 2.8
  });

  gain.gain.value = input.velocity * (0.58 + (variant?.bodyGain ?? 0.7) * 0.18);
  main.output.connect(mainBus);
  mainBus.connect(gain);
  mainBus.connect(jawari);
  jawariHigh.type = "highpass";
  jawariHigh.frequency.setValueAtTime(1100 + brightness * 1800, input.now);
  jawariHigh.Q.setValueAtTime(0.7, input.now);
  jawariGain.gain.setValueAtTime(input.velocity * (0.052 + jawariDrive * 0.065), input.now);
  jawariGain.gain.setTargetAtTime(0.012, input.now + 0.2, 0.85);
  jawari.connect(jawariHigh);
  jawariHigh.connect(jawariGain);
  jawariGain.connect(gain);
  cleanupNodes.push(...main.cleanupNodes, jawari, jawariHigh, jawariGain, ...sympathetic.cleanupNodes);
  sources.push(main.source, ...sympathetic.sources);

  const mizraab = connectClick(input, gain, 5200 + brightness * 1800, input.velocity * 0.076, 0.007, 4.5);
  sources.push(mizraab.source);
  cleanupNodes.push(...mizraab.cleanupNodes);

  if (meend) {
    scheduleDelayPitch(main.delay, input, [
      { time: 0, cents: 0 },
      { time: 0.36, cents: meendDepth },
      { time: 0.66, cents: meendDepth * 0.72 }
    ]);
    jawariHigh.frequency.linearRampToValueAtTime(2400 + brightness * 3600, input.now + 0.32);
    jawariGain.gain.linearRampToValueAtTime(input.velocity * (0.11 + jawariDrive * 0.06), input.now + 0.32);
  }

  if (input.ornament === "chikari") {
    const chikari = createModalBank(context, input.frequency * 2.0, [
      { ratio: 1, gain: 0.09, decay: 0.34 },
      { ratio: 2.01, gain: 0.035, decay: 0.24 },
      { ratio: 3.02, gain: 0.02, decay: 0.18 }
    ], {
      destination: gain,
      velocity: input.velocity,
      now: input.now,
      family: "plucked_string_waveguide",
      duration: 1.0
    });

    sources.push(...chikari.sources);
    cleanupNodes.push(...chikari.cleanupNodes);
  }

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: input.variantId === "surbahar" ? 1.2 : 0.86,
    debug: {
      family: "plucked_string_waveguide",
      model: "sitar_jawari_sympathetic_waveguide",
      variantTags: variant?.metadataTags,
      layers: ["mizraab", "karplus_main", "amplitude_dependent_jawari", "sympathetic_bank"]
    }
  });
}

export function renderKora(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const variant = getVariantModelProfile("kora", input.variantId);
  const gain = context.createGain();
  const dry = context.createGain();
  const panner = context.createStereoPanner();
  const sources: AudioScheduledSourceNode[] = [];
  const cleanupNodes: AudioNode[] = [gain, dry, panner];
  const rankPan =
    input.ornament === "left_thumb" || input.ornament === "thumb_index_interlock"
      ? -0.38
      : input.ornament === "right_index"
        ? 0.38
        : 0.12;
  const delayOffset = input.ornament === "thumb_index_interlock" ? 0.014 : 0;
  const primary = createKarplusStrongString(context, input, {
    brightness: variant?.brightness ?? 0.54,
    decay: 0.4 + (variant?.decay ?? 0.7) * 0.52,
    feedback: input.variantId === "electric_modern_kora" ? 0.9 : 0.94,
    pluckPosition: rankPan < 0 ? 0.34 : 0.42,
    outputGain: 0.34,
    seedSuffix: "rank-primary"
  });

  gain.gain.value = input.velocity * (0.54 + (variant?.bodyGain ?? 0.7) * 0.18);
  primary.output.connect(dry);
  dry.connect(panner);
  panner.pan.setValueAtTime(rankPan, input.now);
  panner.connect(gain);
  sources.push(primary.source);
  cleanupNodes.push(...primary.cleanupNodes);
  cleanupNodes.push(
    ...createBodyResonatorBank(context, dry, gain, [
      { frequency: 165, q: 1.1, gainDb: input.variantId === "electric_modern_kora" ? 1.4 : 4.8, type: "peaking" },
      { frequency: 255, q: 1.3, gainDb: input.variantId === "electric_modern_kora" ? 1.0 : 3.8, type: "peaking" },
      { frequency: 720, q: 2.0, gainDb: 2.6, type: "peaking" }
    ], variant?.bodyGain ?? 0.7)
  );

  const click = connectClick(input, gain, rankPan < 0 ? 3300 : 4600, input.velocity * (0.045 + (variant?.noiseGain ?? 0.3) * 0.045), 0.007, 2.8);
  sources.push(click.source);
  cleanupNodes.push(...click.cleanupNodes);

  if (delayOffset > 0) {
    const ghostInput = { ...input, now: input.now + delayOffset };
    const ghost = createKarplusStrongString(context, ghostInput, {
      frequency: input.frequency * centsToRatio(2.5),
      brightness: (variant?.brightness ?? 0.54) + 0.08,
      decay: 0.32 + (variant?.decay ?? 0.7) * 0.4,
      feedback: 0.92,
      pluckPosition: 0.46,
      outputGain: 0.18,
      seedSuffix: "rank-ghost"
    });

    ghost.output.connect(dry);
    sources.push(ghost.source);
    cleanupNodes.push(...ghost.cleanupNodes);
  }

  if (input.variantId !== "electric_modern_kora") {
    const rattle = createNoiseBurst(context, {
      duration: 0.12,
      attack: 0.01,
      decay: 2.1,
      gain: input.velocity * 0.012,
      filterType: "bandpass",
      frequency: 1600,
      q: 1.6,
      seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, "kora-rattle"),
      tilt: "bright",
      startTime: input.now + 0.012
    });

    rattle.output.connect(gain);
    rattle.source.start(input.now + 0.012);
    sources.push(rattle.source);
    cleanupNodes.push(...rattle.cleanupNodes);
  }

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: input.variantId === "electric_modern_kora" ? 0.42 : 0.84,
    debug: {
      family: "plucked_string_waveguide",
      model: "kora_rank_waveguide_calabash",
      variantTags: variant?.metadataTags,
      layers: ["rank_pan", "calabash_body", "skin_resonance", "thumb_index_timing"]
    }
  });
}
